import React, { useState, useEffect } from 'react';
import { Star, Quote, ExternalLink, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLang } from '../context/LanguageContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const GOOGLE_MAPS_URL = "https://www.google.com/maps/place/Vivek+Mudgal+Astrologer/@28.3736108,77.3705312,17z/data=!3m1!4b1!4m6!3m5!1s0x390ce1637c5d2307:0xda873e01d3d73bb!8m2!3d28.3736108!4d77.3731061!16s%2Fg%2F11btx64w9l";

const DEFAULT_REVIEWS = [
  { name: 'Rajesh Sharma', location: 'Delhi', message: 'Vivek ji ki predictions 100% sahi nikli. Career mein jo problem thi, unke remedies se solve ho gayi. Highly recommended!', rating: 5, source: 'google' },
  { name: 'Priya Mehta', location: 'Mumbai', message: 'Marriage ke liye kundli matching karwai thi. Bohot detailed analysis di. Ab happily married hoon. Thank you Vivek ji!', rating: 5, source: 'google' },
  { name: 'Amit Verma', location: 'Jaipur', message: 'Vastu consultation se ghar mein positive energy aa gayi. Business bhi improve hua. Amazing experience!', rating: 5, source: 'google' },
  { name: 'Sunita Devi', location: 'Chandigarh', message: 'Gemstone recommendation se health mein improvement aayi. Vivek ji bahut knowledgeable hain.', rating: 5, source: 'google' },
  { name: 'Manish Kumar', location: 'Faridabad', message: 'Kundli analysis bahut detail mein ki. Sab predictions sach nikli. Guruji ka anubhav aur gyaan kamaal ka hai. Best astrologer in Faridabad!', rating: 5, source: 'google' },
  { name: 'Kavita Rani', location: 'Noida', message: 'Pehle bahut pareshaan thi family problems se. Guruji ne upay bataye, sab theek ho gaya. Bahut acche insaan hain.', rating: 5, source: 'justdial' },
];

export default function Testimonials() {
  const { lang } = useLang();
  const [reviews, setReviews] = useState(DEFAULT_REVIEWS);

  useEffect(() => {
    axios.get(`${API}/api/reviews`).then(res => {
      if (res.data && res.data.length > 0) setReviews(res.data);
    }).catch(() => {});
  }, []);

  return (
    <section className="section-padding relative overflow-hidden" data-testid="testimonials-section">
      <div className="absolute inset-0 cosmic-bg-alt" />
      <div className="absolute top-1/3 right-0 w-96 h-96 bg-purple/8 rounded-full blur-[120px]" />

      <div className="container-custom relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-xs tracking-[0.3em] uppercase text-gold font-semibold font-body mb-4">
            {lang === 'hi' ? 'समीक्षाएं' : 'Reviews'}
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-text-main mb-6 tracking-tight">
            {lang === 'hi' ? (
              <>Google <span className="text-gold-gradient">Reviews</span></>
            ) : (
              <>What Our <span className="text-gold-gradient">Clients</span> Say</>
            )}
          </h2>
          <div className="divine-divider mt-6 max-w-xs mx-auto" />
        </div>

        {/* Google Rating Badge */}
        <div className="flex justify-center mb-10">
          <a
            href={GOOGLE_MAPS_URL}
            target="_blank" rel="noopener noreferrer"
            className="glass-card px-8 py-5 flex items-center gap-5 hover:border-yellow-600/50 transition-all group"
            data-testid="google-rating-badge"
          >
            <div className="flex items-center gap-2">
              <svg viewBox="0 0 24 24" className="w-8 h-8">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="text-text-muted text-sm">Google</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-3xl font-heading text-yellow-400">4.9</span>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
              </div>
              <p className="text-text-muted text-xs mt-0.5">72+ Reviews on Google & Justdial</p>
            </div>
            <ExternalLink className="w-4 h-4 text-text-muted group-hover:text-yellow-400 transition-colors ml-2" />
          </a>
        </div>

        {/* Review Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.map((t, i) => (
            <motion.div key={i} className="glass-card p-6" data-testid={`testimonial-${i}`}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-500 to-yellow-700 flex items-center justify-center text-white font-heading text-sm">
                    {t.name?.charAt(0)}
                  </div>
                  <div>
                    <p className="text-text-main font-medium text-sm">{t.name}</p>
                    <p className="text-xs text-text-muted flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {t.location}
                    </p>
                  </div>
                </div>
                {t.source === 'google' ? (
                  <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                ) : (
                  <span className="text-[10px] text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded-full">Justdial</span>
                )}
              </div>
              <div className="flex gap-0.5 mb-3">
                {[...Array(t.rating)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />)}
              </div>
              <Quote className="w-5 h-5 text-gold/15 mb-1" />
              <p className="text-text-muted text-sm leading-relaxed italic">"{t.message}"</p>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <a
            href={GOOGLE_MAPS_URL}
            target="_blank" rel="noopener noreferrer"
            className="btn-gold-outline inline-flex items-center gap-2"
            data-testid="view-all-reviews-btn"
          >
            {lang === 'hi' ? 'Google पर सभी Reviews देखें' : 'View All Reviews on Google'}
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
